import React from 'react';
import { Thing } from 'schema-dts';

import clsxm from '@/lib/clsxm';
import { Row as HygraphRowProps } from '@/lib/types';

import Row from '@/components/row';

import COMMON_CLASS_NAMES from '@/constants/common-class-names';
import {
  getRenderer,
  getRendererData,
  getSchemaBuilder,
} from '@/hygraph/renderers';
import rowTransformer from '@/hygraph/transformers/row.transformer';
import { TSchemaData } from '@/services/schema-service';

RowRenderer.buildSchema = buildSchema;

function buildSchema(row: HygraphRowProps): TSchemaData<Thing> {
  return row.rowComponents.reduce(
    (schemaData, component) => {
      const buildSchema = getSchemaBuilder(component.__typename);
      if (!buildSchema) return schemaData;
      const { images, questions, things } = buildSchema(component);
      return {
        images: [...schemaData.images, ...(images || [])],
        questions: [...schemaData.questions, ...(questions || [])],
        things: [...schemaData.things, ...(things || [])],
      };
    },
    { images: [], questions: [], things: [] } as TSchemaData<Thing>
  );
}

export default function RowRenderer(props: HygraphRowProps) {
  const rowData = rowTransformer(props);

  if (!rowData) {
    return <></>;
  }

  const sidebarComponents = props.rowComponents.filter(
    (component) => component.__typename === 'Sidebar'
  );

  const rowWithComponents = (
    <Row key={rowData.slug || props.id} {...rowData}>
      {props.rowComponents.map((component, index) => {
        const Renderer = getRenderer(component.__typename);
        if (!Renderer) return <></>;

        if (component.__typename === 'Sidebar') {
          return (
            <span key={component.id || index} className='lg:hidden'>
              <Renderer
                key={component.id}
                {...getRendererData({ ...component, index })}
              />
            </span>
          );
        }

        return (
          <Renderer
            key={component.id}
            {...getRendererData({ ...component, index })}
          />
        );
      })}
    </Row>
  );

  if (sidebarComponents.length > 0) {
    return (
      <div
        className={clsxm(
          'flex',
          COMMON_CLASS_NAMES.mainContentContainerWidth,
          'px-0 sm:px-0'
        )}
      >
        <div className='flex-1'>{rowWithComponents}</div>
        <div className='ml-6 hidden w-72 pr-6 lg:inline-block'>
          {sidebarComponents.map((component, index) => {
            const Renderer = getRenderer(component.__typename);
            if (!Renderer) return <></>;

            const props = { ...getRendererData({ ...component, index }) };

            if (component.__typename === 'Card') {
              props.className = clsxm(props.className || '', 'h-auto');
            }

            return (
              <span
                key={component.id || index}
                className={clsxm(
                  'h-fit w-fit',
                  component.__typename === 'Sidebar' && 'lg:visible'
                )}
              >
                <Renderer {...props} />
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  return rowWithComponents;
}
